import React, { useState } from 'react';
import {
  QrCode,
  Shield,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Camera,
  MapPin,
  ListChecks,
  CheckSquare,
  Square,
  Printer,
  ChevronRight,
  Sparkles,
  Search,
  Filter,
  Check,
  AlertCircle,
  Eye,
  Crosshair,
  Volume2
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { PatrolCheckpoint, PatrolScanLog, PatrolStatus } from '../types';
import { CheckpointQRCardsModal } from './CheckpointQRCardsModal';

export const SecurityPatrolScanner: React.FC = () => {
  const { currentUser, patrolCheckpoints, patrolLogs, recordPatrolScan } = useApp();

  // Active Round Selection
  const [activeRound, setActiveRound] = useState<{ number: number; timeSlot: string }>({
    number: 1,
    timeSlot: 'รอบเช้า 08:00 น.',
  });

  // Modals & Scanner UI states
  const [showQRCardModal, setShowQRCardModal] = useState(false);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [scannedCheckpoint, setScannedCheckpoint] = useState<PatrolCheckpoint | null>(null);

  // Manual QR input or camera simulation state
  const [manualQRInput, setManualQRInput] = useState('');
  const [scannerError, setScannerError] = useState<string | null>(null);

  // Inspection Form State
  const [checklistValues, setChecklistValues] = useState<{ [key: string]: boolean }>({});
  const [inspectionStatus, setInspectionStatus] = useState<PatrolStatus>('normal');
  const [inspectionNote, setInspectionNote] = useState('');
  const [inspectionPhoto, setInspectionPhoto] = useState<string>('');
  const [actionSuccessNotice, setActionSuccessNotice] = useState<string | null>(null);

  // Filter for history
  const [historyFilter, setHistoryFilter] = useState<'all' | 'abnormal' | 'round1'>('all');

  // Available patrol rounds in a 24-hour cycle
  const PATROL_ROUNDS = [
    { number: 1, timeSlot: 'รอบเช้า 08:00 น.', label: 'กะเช้า' },
    { number: 2, timeSlot: 'รอบเที่ยง 12:00 น.', label: 'กะเช้า' },
    { number: 3, timeSlot: 'รอบเย็น 16:00 น.', label: 'กะเช้า' },
    { number: 4, timeSlot: 'รอบค่ำ 20:00 น.', label: 'กะดึก' },
    { number: 5, timeSlot: 'รอบดึก 00:00 น.', label: 'กะดึก' },
    { number: 6, timeSlot: 'รอบรุ่งสาง 04:00 น.', label: 'กะดึก' },
  ];

  // Calculate scanned checkpoints for current active round
  const currentRoundScans = patrolLogs.filter((log) => log.round_number === activeRound.number);
  const scannedCheckpointIds = new Set(currentRoundScans.map((log) => log.checkpoint_id));
  const progressPercent = Math.round((scannedCheckpointIds.size / patrolCheckpoints.length) * 100);

  // Open Scanner with a designated checkpoint or generic
  const handleOpenScanner = (cp?: PatrolCheckpoint) => {
    setScannerError(null);
    setManualQRInput('');
    if (cp) {
      handleSelectCheckpointForInspection(cp);
    } else {
      setShowScannerModal(true);
    }
  };

  // Process QR String verification
  const handleVerifyQRData = (qrText: string) => {
    const trimmed = qrText.trim();
    if (!trimmed) return;

    const matched = patrolCheckpoints.find(
      (cp) => cp.qr_data.toLowerCase() === trimmed.toLowerCase() || cp.code.toLowerCase() === trimmed.toLowerCase()
    );

    if (!matched) {
      setScannerError(`ไม่พบรหัส QR จุดตรวจ: "${trimmed}" กรุณาตรวจสอบอีกครั้ง`);
      return;
    }

    setShowScannerModal(false);
    handleSelectCheckpointForInspection(matched);
  };

  // Initialize checklist for chosen checkpoint
  const handleSelectCheckpointForInspection = (cp: PatrolCheckpoint) => {
    const initialChecks: { [key: string]: boolean } = {};
    cp.checklist_items.forEach((item) => {
      initialChecks[item] = true;
    });
    setChecklistValues(initialChecks);
    setInspectionStatus('normal');
    setInspectionNote('');
    setInspectionPhoto(cp.image_url || '');
    setScannedCheckpoint(cp);
  };

  // Toggle checklist item
  const handleToggleCheck = (item: string) => {
    setChecklistValues((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  // Submit Patrol Scan Log
  const handleSubmitScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scannedCheckpoint) return;

    // Check if any check failed; if so, recommend abnormal or keep user choice
    const hasUnchecked = Object.values(checklistValues).some((v) => v === false);
    const finalStatus: PatrolStatus = hasUnchecked ? 'abnormal' : inspectionStatus;

    const result = await recordPatrolScan({
      checkpoint_id: scannedCheckpoint.id,
      round_number: activeRound.number,
      round_time_slot: activeRound.timeSlot,
      status: finalStatus,
      checklist_results: checklistValues,
      note: inspectionNote || (finalStatus === 'normal' ? 'ตรวจความเรียบร้อยรอบพื้นที่ ปกติดี' : 'พบข้อบกพร่องตามรายการตรวจ'),
      photo_url: inspectionPhoto || scannedCheckpoint.image_url,
      location_verified: true,
    });

    if (result) {
      setActionSuccessNotice(
        `บันทึกผลการสแกนตรวจจุด "${scannedCheckpoint.name}" (${result.scanned_time}) เรียบร้อยแล้ว!`
      );
      setScannedCheckpoint(null);
    }
  };

  // Filtered Logs
  const filteredLogs = patrolLogs.filter((log) => {
    if (historyFilter === 'abnormal') return log.status === 'abnormal';
    if (historyFilter === 'round1') return log.round_number === activeRound.number;
    return true;
  });

  return (
    <div id="security-patrol-section" className="space-y-6">
      {/* Action Notification Banner */}
      {actionSuccessNotice && (
        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs flex items-center justify-between shadow-xs animate-fadeIn">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span className="font-semibold text-sm">{actionSuccessNotice}</span>
          </div>
          <button
            onClick={() => setActionSuccessNotice(null)}
            className="text-slate-400 hover:text-slate-700 font-bold px-2"
          >
            ✕
          </button>
        </div>
      )}

      {/* Hero Patrol Header Card */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-teal-950 rounded-3xl p-6 text-white shadow-xl border border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
        <div className="relative z-10 space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold backdrop-blur-xs border border-emerald-500/30">
            <Shield className="w-3.5 h-3.5" />
            <span>ระบบตรวจเวรยามดิจิทัล (Smart QR Guard Tour Patrol)</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold">
            ระบบลาดตระเวนสแกน QR Code ประจำจุดตรวจความปลอดภัย
          </h2>
          <p className="text-slate-300 text-xs sm:text-sm max-w-2xl leading-relaxed">
            สแกน QR Code ตามจุดตรวจทั้ง 7 โซนทั่วโครงการ บันทึกรายการตรวจเช็คแบบเรียลไทม์ พร้อมระบบแจ้งเตือนกรณีพบสิ่งผิดปกติ
          </p>
        </div>

        <div className="relative z-10 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowQRCardModal(true)}
            className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold border border-white/20 flex items-center gap-2 transition-all shadow-sm"
          >
            <Printer className="w-4 h-4 text-emerald-300" />
            พิมพ์แผ่นป้าย QR Code
          </button>

          <button
            onClick={() => handleOpenScanner()}
            className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-xl text-xs sm:text-sm flex items-center gap-2 transition-all shadow-lg shadow-emerald-950/40"
          >
            <QrCode className="w-4 h-4" />
            เปิดกล้องสแกน QR Code
          </button>
        </div>
      </div>

      {/* Active Patrol Round Bar */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              เลือกรอบตรวจลาดตระเวน (Patrol Round)
            </span>
            <h3 className="text-slate-900 font-bold text-base sm:text-lg flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-600" />
              {activeRound.timeSlot} (ความคืบหน้า: {scannedCheckpointIds.size}/{patrolCheckpoints.length} จุด)
            </h3>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600">สแกนครบแล้ว:</span>
            <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full">
              {progressPercent}%
            </span>
          </div>
        </div>

        {/* Round Selector Pills */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {PATROL_ROUNDS.map((r) => {
            const count = patrolLogs.filter((log) => log.round_number === r.number).length;
            const isSelected = activeRound.number === r.number;
            return (
              <button
                key={r.number}
                type="button"
                onClick={() => setActiveRound({ number: r.number, timeSlot: r.timeSlot })}
                className={`p-3 rounded-2xl text-left border transition-all ${
                  isSelected
                    ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-500/20 text-emerald-950 font-bold shadow-xs'
                    : 'border-slate-200 bg-slate-50/50 hover:bg-white text-slate-700'
                }`}
              >
                <div className="text-xs font-bold">รอบที่ {r.number}</div>
                <div className="text-[11px] text-emerald-700 font-mono mt-0.5">{r.timeSlot.split(' ')[1]}</div>
                <div className="text-[10px] text-slate-400 mt-1 flex items-center justify-between">
                  <span>{r.label}</span>
                  <span className="font-semibold text-slate-600">{count} จุด</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="space-y-1.5 pt-1">
          <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden border border-slate-200">
            <div
              className="bg-gradient-to-r from-emerald-600 to-teal-500 h-full rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between text-[11px] text-slate-500">
            <span>จุดเริ่มต้น: ป้อม 1</span>
            <span>เป้าหมายรอบนี้: ตรวจครบ 7 จุด ({progressPercent}%)</span>
            <span>จุดสิ้นสุด: สถานีไฟฟ้า MDB</span>
          </div>
        </div>
      </div>

      {/* Checkpoints Grid Cards */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-600" />
            รายการจุดตรวจ Checkpoints ทั้ง 7 โซน
          </h3>
          <span className="text-xs text-slate-500">กดที่จุดตรวจเพื่อจำลองการสแกน QR Code</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {patrolCheckpoints.map((cp) => {
            const scan = currentRoundScans.find((s) => s.checkpoint_id === cp.id);
            const isScanned = Boolean(scan);
            const isAbnormal = scan?.status === 'abnormal';

            return (
              <div
                key={cp.id}
                className={`bg-white rounded-3xl border p-5 flex flex-col justify-between transition-all shadow-xs relative overflow-hidden group ${
                  isAbnormal
                    ? 'border-rose-300 ring-2 ring-rose-400/30'
                    : isScanned
                    ? 'border-emerald-400/80 bg-emerald-50/20'
                    : 'border-slate-200 hover:border-emerald-300 hover:shadow-md'
                }`}
              >
                <div className="space-y-3">
                  {/* Top Badges */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="px-2.5 py-0.5 bg-slate-900 text-white font-mono font-bold text-[10px] rounded-md">
                        {cp.code}
                      </span>
                      <span className="text-[11px] text-slate-500 font-medium ml-2">
                        จุดที่ {cp.sequence} • {cp.zone}
                      </span>
                    </div>

                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 ${
                        isAbnormal
                          ? 'bg-rose-100 text-rose-800'
                          : isScanned
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {isAbnormal ? (
                        <>
                          <AlertTriangle className="w-3 h-3 text-rose-600" />
                          พบสิ่งผิดปกติ
                        </>
                      ) : isScanned ? (
                        <>
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          สแกนแล้ว
                        </>
                      ) : (
                        'รอตรวจ'
                      )}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base leading-snug">
                      {cp.name}
                    </h4>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                      {cp.description}
                    </p>
                  </div>

                  {/* Checklist summary count */}
                  <div className="bg-slate-50 p-2.5 rounded-2xl border border-slate-100 text-xs space-y-1">
                    <span className="font-semibold text-slate-700 flex items-center gap-1 text-[11px]">
                      <ListChecks className="w-3.5 h-3.5 text-emerald-600" />
                      รายการตรวจเช็คประจำจุด ({cp.checklist_items.length} รายการ):
                    </span>
                    <ul className="text-[11px] text-slate-600 space-y-0.5 pl-4 list-disc">
                      {cp.checklist_items.slice(0, 2).map((item, idx) => (
                        <li key={idx} className="truncate">{item}</li>
                      ))}
                      {cp.checklist_items.length > 2 && (
                        <li className="text-slate-400">และอีก {cp.checklist_items.length - 2} รายการ...</li>
                      )}
                    </ul>
                  </div>

                  {/* Last scan info if scanned */}
                  {scan && (
                    <div className="text-[11px] p-2 bg-emerald-50/80 rounded-xl border border-emerald-200 text-emerald-900 space-y-0.5">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">สแกนโดย: {scan.officer_name}</span>
                        <span className="font-mono font-bold text-emerald-700">{scan.scanned_time}</span>
                      </div>
                      {scan.note && <p className="text-slate-600 italic">"{scan.note}"</p>}
                    </div>
                  )}
                </div>

                {/* Bottom Action Button */}
                <div className="pt-4 mt-3 border-t border-slate-100 flex items-center justify-between">
                  <div className="text-[10px] font-mono text-slate-400">
                    QR: {cp.qr_data}
                  </div>

                  <button
                    onClick={() => handleOpenScanner(cp)}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs ${
                      isScanned
                        ? 'bg-slate-800 hover:bg-slate-900 text-white'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                  >
                    <QrCode className="w-3.5 h-3.5" />
                    {isScanned ? 'สแกนตรวจซ้ำ' : 'สแกนจุดตรวจนี้'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Patrol Scan History Table */}
      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="p-5 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-700" />
            <h4 className="font-bold text-slate-900 text-sm sm:text-base">
              สมุดบันทึกประวัติการเดินตรวจเวรยาม รปภ. (Guard Patrol Audit Logs)
            </h4>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-2">
            <div className="inline-flex p-1 bg-slate-200/70 rounded-xl text-xs font-medium">
              <button
                type="button"
                onClick={() => setHistoryFilter('all')}
                className={`px-3 py-1 rounded-lg transition-all ${
                  historyFilter === 'all' ? 'bg-white text-slate-900 font-bold shadow-xs' : 'text-slate-600'
                }`}
              >
                บันทึกทั้งหมด ({patrolLogs.length})
              </button>
              <button
                type="button"
                onClick={() => setHistoryFilter('abnormal')}
                className={`px-3 py-1 rounded-lg transition-all ${
                  historyFilter === 'abnormal' ? 'bg-white text-rose-700 font-bold shadow-xs' : 'text-slate-600'
                }`}
              >
                เฉพาะเหตุผิดปกติ ({patrolLogs.filter((l) => l.status === 'abnormal').length})
              </button>
              <button
                type="button"
                onClick={() => setHistoryFilter('round1')}
                className={`px-3 py-1 rounded-lg transition-all ${
                  historyFilter === 'round1' ? 'bg-white text-slate-900 font-bold shadow-xs' : 'text-slate-600'
                }`}
              >
                รอบที่ {activeRound.number} ({currentRoundScans.length})
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50/60 text-slate-500 font-semibold border-b border-slate-200">
              <tr>
                <th className="p-3.5">จุดตรวจ (Checkpoint)</th>
                <th className="p-3.5">รอบการตรวจ</th>
                <th className="p-3.5">เวลาที่สแกน</th>
                <th className="p-3.5">รปภ. ผู้ตรวจ</th>
                <th className="p-3.5">สถานะ</th>
                <th className="p-3.5">ผลการตรวจเช็ค / หมายเหตุ</th>
                <th className="p-3.5">พิกัด GPS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="p-3.5">
                    <div>
                      <span className="font-mono font-bold text-emerald-700 mr-2">{log.checkpoint_code}</span>
                      <span className="font-semibold text-slate-900">{log.checkpoint_name}</span>
                      <p className="text-[10px] text-slate-400">{log.checkpoint_zone}</p>
                    </div>
                  </td>
                  <td className="p-3.5">
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md font-medium text-[11px]">
                      {log.round_time_slot}
                    </span>
                  </td>
                  <td className="p-3.5 font-mono font-bold text-slate-800">
                    {log.scanned_time}
                  </td>
                  <td className="p-3.5 font-medium text-slate-800">
                    {log.officer_name}
                  </td>
                  <td className="p-3.5">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 w-fit ${
                        log.status === 'normal'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-rose-100 text-rose-800'
                      }`}
                    >
                      {log.status === 'normal' ? 'ปกติ' : 'พบข้อผิดปกติ'}
                    </span>
                  </td>
                  <td className="p-3.5 max-w-xs">
                    <p className="text-slate-700 truncate">{log.note || '-'}</p>
                    {log.photo_url && (
                      <span className="text-[10px] text-blue-600 font-semibold flex items-center gap-1 mt-0.5">
                        <Eye className="w-3 h-3" />
                        มีภาพถ่ายแนบ
                      </span>
                    )}
                  </td>
                  <td className="p-3.5">
                    <span className="text-[11px] text-emerald-700 font-medium flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" />
                      Verified
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL 1: QR CAMERA SCANNER SIMULATOR */}
      {showScannerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-slate-900 rounded-3xl border border-slate-700 shadow-2xl max-w-md w-full overflow-hidden text-white">
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-base">กล้องสแกน QR Code ประจำจุด</h3>
              </div>
              <button
                onClick={() => setShowScannerModal(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg"
              >
                ✕
              </button>
            </div>

            {/* Camera Viewfinder */}
            <div className="relative p-6 flex flex-col items-center justify-center bg-slate-950 aspect-4/3 overflow-hidden">
              <div className="relative w-64 h-64 border-2 border-emerald-500/60 rounded-3xl p-4 flex flex-col items-center justify-center bg-slate-900/40">
                {/* Reticle corner marks */}
                <div className="absolute top-2 left-2 w-6 h-6 border-t-4 border-l-4 border-emerald-400 rounded-tl-lg" />
                <div className="absolute top-2 right-2 w-6 h-6 border-t-4 border-r-4 border-emerald-400 rounded-tr-lg" />
                <div className="absolute bottom-2 left-2 w-6 h-6 border-b-4 border-l-4 border-emerald-400 rounded-bl-lg" />
                <div className="absolute bottom-2 right-2 w-6 h-6 border-b-4 border-r-4 border-emerald-400 rounded-br-lg" />

                {/* Laser animation bar */}
                <div className="w-full h-0.5 bg-emerald-400 shadow-[0_0_12px_#34d399] animate-pulse" />

                <div className="mt-4 text-center">
                  <Crosshair className="w-8 h-8 text-emerald-400 mx-auto animate-spin" />
                  <p className="text-xs text-slate-300 mt-2">หันกล้องไปที่แผ่นป้าย QR Code ประจำจุด</p>
                </div>
              </div>

              {scannerError && (
                <div className="absolute bottom-4 left-4 right-4 bg-rose-500/90 text-white p-2.5 rounded-xl text-xs text-center">
                  {scannerError}
                </div>
              )}
            </div>

            {/* Quick Test Picker for instant realistic test */}
            <div className="p-5 space-y-4 bg-slate-900">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  จำลองการสแกนด่วน (Quick Test Point Selector):
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                  {patrolCheckpoints.map((cp) => (
                    <button
                      key={cp.id}
                      type="button"
                      onClick={() => handleVerifyQRData(cp.qr_data)}
                      className="p-2 bg-slate-800 hover:bg-emerald-900/60 border border-slate-700 hover:border-emerald-500 rounded-xl text-left text-xs transition-colors"
                    >
                      <div className="font-mono font-bold text-emerald-400">{cp.code}</div>
                      <div className="text-[11px] text-slate-200 truncate">{cp.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Manual Input form */}
              <div className="pt-2 border-t border-slate-800">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleVerifyQRData(manualQRInput);
                  }}
                  className="flex gap-2"
                >
                  <input
                    type="text"
                    placeholder="กรอกโค้ด เช่น NRW-CP01 หรือ สแกน..."
                    value={manualQRInput}
                    onChange={(e) => setManualQRInput(e.target.value)}
                    className="flex-1 px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white outline-none focus:border-emerald-500 font-mono"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition-colors"
                  >
                    ยืนยัน
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: CHECKPOINT INSPECTION CHECKLIST DIALOG */}
      {scannedCheckpoint && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden my-auto">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-950 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-emerald-600 text-white text-[10px] font-mono font-bold rounded">
                      {scannedCheckpoint.code}
                    </span>
                    <span className="text-xs text-slate-300">
                      {activeRound.timeSlot}
                    </span>
                  </div>
                  <h3 className="font-bold text-base sm:text-lg text-white mt-0.5">
                    {scannedCheckpoint.name}
                  </h3>
                </div>
              </div>

              <button
                onClick={() => setScannedCheckpoint(null)}
                className="text-slate-300 hover:text-white p-1"
              >
                ✕
              </button>
            </div>

            {/* Checklist Form */}
            <form onSubmit={handleSubmitScan} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="block text-xs font-bold text-slate-700">
                  รายการตรวจเช็คความปลอดภัยประจำจุด (Safety Checklist):
                </label>
                <div className="space-y-2 bg-slate-50 p-3.5 rounded-2xl border border-slate-200">
                  {scannedCheckpoint.checklist_items.map((item, idx) => {
                    const isChecked = checklistValues[item] !== false;
                    return (
                      <div
                        key={idx}
                        onClick={() => handleToggleCheck(item)}
                        className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-all ${
                          isChecked
                            ? 'bg-white border-emerald-300 text-slate-800 shadow-2xs'
                            : 'bg-rose-50/60 border-rose-300 text-rose-900'
                        }`}
                      >
                        <div className="shrink-0">
                          {isChecked ? (
                            <CheckSquare className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <Square className="w-4 h-4 text-rose-500" />
                          )}
                        </div>
                        <span className="text-xs font-medium leading-tight select-none">
                          {item}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Status Toggle */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  สรุปผลการตรวจจุดนี้:
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setInspectionStatus('normal')}
                    className={`py-2.5 px-4 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-2 ${
                      inspectionStatus === 'normal'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <Check className="w-4 h-4" />
                    ปกติ เรียบร้อย (Normal)
                  </button>

                  <button
                    type="button"
                    onClick={() => setInspectionStatus('abnormal')}
                    className={`py-2.5 px-4 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-2 ${
                      inspectionStatus === 'abnormal'
                        ? 'bg-rose-600 text-white border-rose-600 shadow-xs'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4" />
                    พบข้อผิดปกติ (Incident)
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  บันทึกรายละเอียด / ข้อสังเกตของ รปภ.:
                </label>
                <textarea
                  rows={2}
                  value={inspectionNote}
                  onChange={(e) => setInspectionNote(e.target.value)}
                  placeholder="ระบุสิ่งที่พบ เช่น ประตูล็อกแน่นหนา, ไฟดับ 1 จุด, สภาพปกติ..."
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>

              {/* Officer & GPS info */}
              <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-200 text-[11px] text-emerald-900 flex items-center justify-between">
                <span>รปภ. ผู้ตรวจ: <b>{currentUser?.full_name || 'รปภ. ประจำเวร'}</b></span>
                <span className="text-emerald-700 font-mono">GPS Verified ✓</span>
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setScannedCheckpoint(null)}
                  className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-md transition-colors flex items-center gap-1.5"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  บันทึกผลการตรวจเวร
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Printable Checkpoint Signs Modal */}
      <CheckpointQRCardsModal
        checkpoints={patrolCheckpoints}
        isOpen={showQRCardModal}
        onClose={() => setShowQRCardModal(false)}
      />
    </div>
  );
};
